import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { API_BASE } from '../lib/apiBase';
import { useAuthStore } from '../lib/auth';

interface ManualLookupResult {
  manualslib_url: string;
  manufacturer_url: string | null;
  search_query: string;
}

interface ExtractedTask {
  task_name: string;
  frequency: string;
  frequency_days: number;
  description: string;
  diy_difficulty: 'easy' | 'medium' | 'hard' | 'pro_only';
  warning_if_skipped: string | null;
}

interface ExtractedMaintenance {
  appliance_type: string;
  brand: string;
  model: string;
  maintenance_tasks: ExtractedTask[];
  annual_service_required: boolean;
  filter_replacement_days: number | null;
  warranty_maintenance_requirements: string | null;
}

interface ExtractionResult {
  extracted: ExtractedMaintenance;
  tasks_created: number;
}

export interface ManualFinderProps {
  applianceId: string;
  brand: string | null;
  model: string | null;
  onDone: () => void;
  onSkip: () => void;
}

type Step = 'loading' | 'idle' | 'extracting' | 'confirming' | 'done' | 'error';

const DIFFICULTY_LABEL: Record<string, string> = {
  easy: 'Easy DIY',
  medium: 'Moderate DIY',
  hard: 'Hire a Pro',
  pro_only: 'Hire a Pro',
};

const DIFFICULTY_COLOR: Record<string, string> = {
  easy: '#16a34a',
  medium: '#d97706',
  hard: '#dc2626',
  pro_only: '#dc2626',
};

function isValidUrl(s: string): boolean {
  try {
    const url = new URL(s);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

export function ManualFinder({ applianceId, brand, model, onDone, onSkip }: ManualFinderProps) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const [step, setStep] = useState<Step>('loading');
  const [urls, setUrls] = useState<ManualLookupResult | null>(null);
  const [manualUrl, setManualUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [extractionResult, setExtractionResult] = useState<ExtractionResult | null>(null);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!brand && !model) {
      setStep('idle');
      return;
    }
    api.get<ManualLookupResult>(`/appliances/${applianceId}/manual`)
      .then((result) => { setUrls(result); setStep('idle'); })
      .catch(() => { setStep('idle'); });
  }, [applianceId, brand, model]);

  function openUrl(url: string) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  function buildGoogleUrl(): string {
    const q = encodeURIComponent(`${brand ?? ''} ${model ?? ''} owner manual PDF`.trim());
    return `https://www.google.com/search?q=${q}`;
  }

  async function handleExtract() {
    setUrlError('');
    const trimmed = manualUrl.trim();
    if (!trimmed) {
      setUrlError('Please paste a URL first.');
      return;
    }
    if (!isValidUrl(trimmed)) {
      setUrlError('That doesn\'t look like a valid URL. It should start with https://');
      return;
    }

    setStep('extracting');
    try {
      const res = await fetch(`${API_BASE}/appliances/${applianceId}/manual/extract-maintenance`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ manual_url: trimmed }),
      });

      const data = await res.json() as ExtractionResult & { error?: { message?: string } };
      if (!res.ok) {
        setErrorMsg(data.error?.message ?? 'Could not read that manual — please try another URL or enter tasks manually.');
        setStep('error');
        return;
      }

      setExtractionResult(data);
      setStep('confirming');
    } catch {
      setErrorMsg('Network error — check your connection and try again.');
      setStep('error');
    }
  }

  const displayBrand = brand ?? '';
  const displayModel = model ?? '';

  // ── LOADING ──
  if (step === 'loading') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
        <div style={{
          width: 28, height: 28, border: '3px solid #e9d5ff',
          borderTopColor: '#9333ea', borderRadius: '50%',
          animation: 'spin 0.7s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── EXTRACTING ──
  if (step === 'extracting') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '32px 0' }}>
        <div style={{
          width: 52, height: 52, border: '4px solid #e9d5ff',
          borderTopColor: '#9333ea', borderRadius: '50%',
          animation: 'spin 0.75s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#374151', textAlign: 'center' }}>
          Claude is reading your manual…
        </p>
        <p style={{ margin: 0, fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>
          Extracting maintenance schedule — this takes about 10 seconds
        </p>
      </div>
    );
  }

  // ── DONE ──
  if (step === 'done') {
    const count = extractionResult?.tasks_created ?? 0;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '32px 20px' }}>
        <div style={{
          width: 72, height: 72, background: '#dcfce7', borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36,
        }}>
          ✅
        </div>
        <p style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#15803d', textAlign: 'center' }}>
          {count} maintenance task{count !== 1 ? 's' : ''} added!
        </p>
        <p style={{ margin: 0, fontSize: 14, color: '#6b7280', textAlign: 'center' }}>
          You can view and manage them in your task list.
        </p>
        <button
          onClick={onDone}
          style={{
            width: '100%', padding: '12px',
            background: '#9333ea', color: '#fff',
            border: 'none', borderRadius: 12,
            fontSize: 15, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Go to task list
        </button>
      </div>
    );
  }

  // ── ERROR ──
  if (step === 'error') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '8px 0' }}>
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: '12px 16px' }}>
          <p style={{ margin: 0, fontSize: 14, color: '#dc2626', fontWeight: 600 }}>Could not extract maintenance schedule</p>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#dc2626' }}>{errorMsg}</p>
        </div>
        <button
          onClick={() => setStep('idle')}
          style={{
            padding: '11px', background: '#fff', color: '#374151',
            border: '1px solid #d1d5db', borderRadius: 12,
            fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Try a different URL
        </button>
        <button
          onClick={onSkip}
          style={{ padding: '11px', background: 'none', color: '#9ca3af', border: 'none', fontSize: 14, cursor: 'pointer' }}
        >
          Skip for now
        </button>
      </div>
    );
  }

  // ── CONFIRMING ──
  if (step === 'confirming' && extractionResult) {
    const { extracted, tasks_created } = extractionResult;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '12px 16px' }}>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#15803d' }}>
            Found {tasks_created} maintenance task{tasks_created !== 1 ? 's' : ''}
          </p>
          <p style={{ margin: '3px 0 0', fontSize: 12, color: '#166534' }}>
            {extracted.brand} {extracted.model}
            {extracted.annual_service_required ? ' · Annual service recommended' : ''}
          </p>
        </div>

        {extracted.maintenance_tasks.slice(0, 6).map((task, i) => (
          <div key={i} style={{ background: '#f9fafb', borderRadius: 12, padding: '12px 14px', border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827', flex: 1 }}>{task.task_name}</p>
              <span style={{
                fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 8, flexShrink: 0,
                background: DIFFICULTY_COLOR[task.diy_difficulty] + '18',
                color: DIFFICULTY_COLOR[task.diy_difficulty],
              }}>
                {DIFFICULTY_LABEL[task.diy_difficulty]}
              </span>
            </div>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6b7280' }}>{task.frequency}</p>
            {task.description && (
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
                {task.description.length > 120 ? task.description.slice(0, 120) + '…' : task.description}
              </p>
            )}
          </div>
        ))}

        {extracted.maintenance_tasks.length > 6 && (
          <p style={{ margin: 0, fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>
            +{extracted.maintenance_tasks.length - 6} more tasks
          </p>
        )}

        {extracted.warranty_maintenance_requirements && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '10px 14px' }}>
            <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: '#92400e' }}>Warranty note</p>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: '#78350f', lineHeight: 1.5 }}>
              {extracted.warranty_maintenance_requirements}
            </p>
          </div>
        )}

        <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827', textAlign: 'center' }}>
          Add these tasks to your schedule?
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onSkip}
            style={{
              flex: 1, padding: '12px', background: '#fff', color: '#374151',
              border: '1px solid #d1d5db', borderRadius: 12,
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Skip
          </button>
          <button
            onClick={() => setStep('done')}
            style={{
              flex: 2, padding: '12px', background: '#9333ea', color: '#fff',
              border: 'none', borderRadius: 12,
              fontSize: 14, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Yes, add them
          </button>
        </div>
      </div>
    );
  }

  // ── IDLE (Step 1 + Step 2) ──
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Step 1 */}
      <div>
        <p style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 700, color: '#111827' }}>
          Step 1 — Find your manual
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => openUrl(urls?.manualslib_url ?? `https://www.manualslib.com/search/?q=${encodeURIComponent(`${displayBrand} ${displayModel}`)}`)}
            style={{
              flex: 1, padding: '12px 10px',
              background: '#f3f4f6', color: '#111827',
              border: '1px solid #e5e7eb', borderRadius: 12,
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            }}
          >
            <span style={{ fontSize: 20 }}>🔍</span>
            <span>ManualsLib</span>
          </button>

          <button
            onClick={() => openUrl(urls?.manufacturer_url ?? buildGoogleUrl())}
            style={{
              flex: 1, padding: '12px 10px',
              background: '#f3f4f6', color: '#111827',
              border: '1px solid #e5e7eb', borderRadius: 12,
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            }}
          >
            <span style={{ fontSize: 20 }}>🏭</span>
            <span>{urls?.manufacturer_url ? 'Manufacturer' : 'Google Search'}</span>
          </button>
        </div>
        <p style={{ margin: '10px 0 0', fontSize: 12, color: '#6b7280', textAlign: 'center', lineHeight: 1.5 }}>
          Find your manual, then paste the PDF or page link below to automatically extract your maintenance schedule.
        </p>
      </div>

      {/* Step 2 */}
      <div>
        <p style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 700, color: '#111827' }}>
          Step 2 — Paste manual link <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span>
        </p>
        <input
          type="url"
          value={manualUrl}
          onChange={(e) => { setManualUrl(e.target.value); setUrlError(''); }}
          placeholder="https://…"
          style={{
            width: '100%', padding: '11px 14px',
            borderRadius: 10, border: urlError ? '1.5px solid #ef4444' : '1.5px solid #d1d5db',
            fontSize: 14, boxSizing: 'border-box', outline: 'none',
          }}
        />
        {urlError && <p style={{ margin: '4px 0 0', fontSize: 12, color: '#dc2626' }}>{urlError}</p>}

        <button
          onClick={handleExtract}
          disabled={!manualUrl.trim()}
          style={{
            width: '100%', padding: '13px', marginTop: 10,
            background: manualUrl.trim() ? '#9333ea' : '#e9d5ff',
            color: '#fff', border: 'none', borderRadius: 12,
            fontSize: 15, fontWeight: 700,
            cursor: manualUrl.trim() ? 'pointer' : 'not-allowed',
            transition: 'background 0.15s',
          }}
        >
          Extract maintenance schedule
        </button>
      </div>

      <button
        onClick={onSkip}
        style={{ padding: '10px', background: 'none', color: '#9ca3af', border: 'none', fontSize: 13, cursor: 'pointer' }}
      >
        Skip — I'll do this later
      </button>
    </div>
  );
}
